import {useEffect} from "react";
import {isFeaturePanelRoute} from "featurePanelRoutes";
import {isDev} from "utils/ReactHelpers";
import {isPlainPrimaryClick} from "nav/resourceNavigation";
import type {AnalyticsEventName, AnalyticsEventParams} from "analytics/analyticsEvents";

/**
 * FeaturePanel open latency instrumentation (ui#169).
 *
 * Measures "panel-route intent → panel-body first paint": the mark is armed by a document-level
 * CAPTURE-phase click listener the moment the user clicks any link to a feature-panel route (see
 * {@link installPanelIntentListener}, installed once by RootLayout — capture fires BEFORE the router's
 * own click handling, so the mark predates navigation); the measure side is the single
 * {@link usePanelOpenLatency} hook call inside the FeaturePanel, which resolves the pending mark on a
 * double `requestAnimationFrame` after the panel body commits — i.e. right after its first paint.
 *
 * Reporting is two-channel:
 *  - dev: a `console.table` line per open (local only — {@link isDev} gates it out of prod).
 *  - prod: forwarded to the analytics ingest as a {@link PANEL_OPEN_EVENT_NAME} event — but ONLY once
 *    the consent-gated analytics bootstrap has registered a reporter via
 *    {@link setPanelLatencyReporter} (see `analytics/analytics.ts`). Before consent, measures are
 *    dropped, never buffered: nothing leaves the device without an explicit opt-in.
 *
 * The measurement core is a pure factory ({@link createPanelLatencyTracker}) over an injectable
 * performance-like clock, so every timing rule is unit-testable with a fake performance API. Nothing
 * here imports `web-vitals` or `firebase/analytics` — this module stays a few hundred bytes in the
 * eager bundle while the heavy reporting lives in consent-gated lazy chunks.
 */

/**
 * The event name for one panel open (params: {@link PanelOpenEvent}), on the api's wire vocabulary since
 * kitecorp/transferify-api-service#1929. The api maps it back to GA4's `panel_open`.
 */
export const PANEL_OPEN_EVENT_NAME: AnalyticsEventName = "PANEL_OPEN";

/**
 * Intent marks older than this are considered abandoned (navigation cancelled, tab hidden, …) and are
 * discarded instead of producing a nonsense multi-second "latency". Panel opens are sub-second — an
 * order of magnitude of headroom still cleanly separates real opens from stale marks.
 */
export const INTENT_MAX_AGE_MS = 10_000;

/**
 * The minimal slice of the Performance API the tracker needs. `now()` drives the measurement;
 * `mark`/`measure` are optional niceties that surface the spans in DevTools performance traces.
 */
export interface PerfLike {
	now(): number;
	mark?(name: string): unknown;
	measure?(name: string, options?: {start: number; end: number}): unknown;
}

/** One completed panel open: the route and the intent→body-first-paint duration. */
export interface PanelOpenMeasure {
	/** The feature-panel route that was opened (e.g. "/transfers"). */
	path: string;
	/** Milliseconds from the click on the panel-route link to the panel body's first paint. */
	durationMs: number;
}

/** The {@link PANEL_OPEN_EVENT_NAME} analytics event derived from one {@link PanelOpenMeasure}. */
export interface PanelOpenEvent {
	name: AnalyticsEventName;
	params: {panel_path: string; duration_ms: number};
}

/**
 * Create an intent→open tracker over an injectable clock (pure — no globals), so expiry/consume
 * semantics are unit-testable with a fake performance API. One pending mark per path; marking again
 * simply restarts the clock (the newest intent is the one being measured).
 */
export function createPanelLatencyTracker(perf: PerfLike) {
	const pendingByPath = new Map<string, number>();

	/** True when `path` has a live (non-expired) intent mark; expired marks are dropped on read. */
	function hasPendingIntent(path: string): boolean {
		const marked = pendingByPath.get(path);
		if (marked === undefined) {
			return false;
		}
		if (perf.now() - marked > INTENT_MAX_AGE_MS) {
			pendingByPath.delete(path);
			return false;
		}
		return true;
	}

	return {
		/** Arm (or re-arm) the intent mark for a panel route. */
		markIntent(path: string): void {
			pendingByPath.set(path, perf.now());
			perf.mark?.(`panel-intent:${path}`);
		},

		hasPendingIntent,

		/**
		 * Consume the pending intent for `path` into a measure, or `null` when there is none (no mark,
		 * already consumed, or expired). One-shot: a second call for the same open returns `null`.
		 */
		measureOpen(path: string): PanelOpenMeasure | null {
			if (!hasPendingIntent(path)) {
				return null;
			}
			const marked = pendingByPath.get(path)!;
			pendingByPath.delete(path);
			const end = perf.now();
			try {
				perf.measure?.(`panel-open:${path}`, {start: marked, end});
			} catch {
				// DevTools-trace nicety only — older engines without the options signature just skip it.
			}
			return {path, durationMs: end - marked};
		},
	};
}

export type PanelLatencyTracker = ReturnType<typeof createPanelLatencyTracker>;

/** SSG-safe default clock: real `performance` in browsers/Node, a Date fallback elsewhere. */
const defaultPerf: PerfLike = typeof performance !== "undefined" ? performance : {now: () => Date.now()};

/** The app-wide tracker instance shared by the intent listener and the measuring hook. */
export const panelLatencyTracker: PanelLatencyTracker = createPanelLatencyTracker(defaultPerf);

/** The structural slice of a click event {@link panelIntentPathFromClick} inspects (testable sans DOM). */
export interface PanelIntentClickLike {
	button: number;
	metaKey: boolean;
	ctrlKey: boolean;
	shiftKey: boolean;
	altKey: boolean;
	target: unknown;
}

/**
 * Decide whether a click expresses "open this feature panel" intent, returning the panel route or
 * `null`. Pure so the whole decision table is unit-testable:
 *  - primary button only, with no modifier keys (Cmd/Ctrl/Shift/Alt open new tabs/windows — the panel
 *    never opens in THIS document, so a mark would only go stale);
 *  - the click must land on (or inside) an `<a href>` that names a path on this site: root-relative
 *    ("/transfers") or relative to the document ("./transfers", which router links render inside a
 *    storefront subfolder, kitecorp/transferify-ui#3521) — absolute and protocol-relative URLs to other
 *    origins never SPA-navigate;
 *  - the path it resolves to from `currentPathname` (query/hash stripped) must be a feature-panel route
 *    that is not already open. The resolved path is the one the measuring hook keys the open by.
 */
export function panelIntentPathFromClick(click: PanelIntentClickLike, currentPathname: string): string | null {
	if (!isPlainPrimaryClick(click)) {
		return null;
	}
	const target = click.target as {closest?: (selector: string) => {getAttribute(name: string): string | null} | null} | null;
	const anchor = target?.closest?.("a[href]");
	const href = anchor?.getAttribute("href");
	if (!href || href.startsWith("//") || !(href.startsWith("/") || href.startsWith("."))) {
		return null;
	}
	// A placeholder origin: only the path arithmetic matters, and both inputs are paths on this site.
	const pathname = new URL(href, `https://site.invalid${currentPathname || "/"}`).pathname;
	if (!isFeaturePanelRoute(pathname) || pathname === currentPathname) {
		return null;
	}
	return pathname;
}

/**
 * Install the document-level intent listener (capture phase, so the mark is armed before react-router
 * even sees the click). Returns the uninstaller, so a mount effect can pass this function directly as
 * its body. Browser-only by construction — only ever called from an effect.
 */
export function installPanelIntentListener(doc: Document = document): () => void {
	const onClickCapture = (event: MouseEvent) => {
		const path = panelIntentPathFromClick(event, doc.location?.pathname ?? "");
		if (path !== null) {
			panelLatencyTracker.markIntent(path);
		}
	};
	doc.addEventListener("click", onClickCapture, true);
	return () => doc.removeEventListener("click", onClickCapture, true);
}

/** Analytics forwarder shape — the api's event vocabulary, so nothing has to map a name downstream. */
export type PerfEventReporter = (name: AnalyticsEventName, params: AnalyticsEventParams) => void;

/** Set (or clear) by the CONSENT-GATED analytics bootstrap; `null` means "drop prod events". */
let reporter: PerfEventReporter | null = null;

export function setPanelLatencyReporter(fn: PerfEventReporter | null): void {
	reporter = fn;
}

/** Map a measure to its analytics event (pure): whole milliseconds, snake_case params. */
export function toPanelOpenEvent(measure: PanelOpenMeasure): PanelOpenEvent {
	return {
		name: PANEL_OPEN_EVENT_NAME,
		params: {panel_path: measure.path, duration_ms: Math.round(measure.durationMs)},
	};
}

/**
 * Report one completed open: dev builds get a `console.table` line; prod forwards to the reporter
 * registered by the consent-gated analytics bootstrap (a no-op until/unless consent was granted).
 */
export function reportPanelOpen(measure: PanelOpenMeasure): void {
	const event = toPanelOpenEvent(measure);
	if (isDev()) {
		console.table([{panel: event.params.panel_path, "intent → body paint (ms)": event.params.duration_ms}]);
	}
	reporter?.(event.name, event.params);
}

/**
 * The measure side (ui#169): the FeaturePanel's single instrumentation hook. Keyed on the active
 * pathname (the panel body remounts per pathname — tab switches measure too, when their click armed a
 * mark). If an intent mark is pending for this path, wait two animation frames after the body's commit
 * — i.e. just after its first paint — then consume the mark and report. No pending mark (direct URL
 * entry, browser Back, refresh) means no measurement: only real click-to-paint spans are reported.
 */
export function usePanelOpenLatency(pathname: string): void {
	useEffect(() => {
		if (!panelLatencyTracker.hasPendingIntent(pathname)) {
			return undefined;
		}
		let secondFrame = 0;
		const firstFrame = requestAnimationFrame(() => {
			secondFrame = requestAnimationFrame(() => {
				const measure = panelLatencyTracker.measureOpen(pathname);
				if (measure) {
					reportPanelOpen(measure);
				}
			});
		});
		return () => {
			cancelAnimationFrame(firstFrame);
			cancelAnimationFrame(secondFrame);
		};
	}, [pathname]);
}
